import React from 'react';
import { Heart, Clock, ChevronRight, Edit3, Trash2, Flame } from 'lucide-react';

export const RecipeCard = ({ 
  recipe, onOpen, onLike, onEdit, onDelete, isLiked, settings, isAuthor 
}) => {
  const handleLikeClick = (e) => {
    e.stopPropagation();
    if (onLike) onLike(recipe);
  };

  return (
    <div 
      data-testid={`recipe-card-${recipe.id}`}
      className="bg-white dark:bg-stone-900 rounded-[48px] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-700 border border-stone-100 dark:border-stone-800 group cursor-pointer"
      onClick={() => onOpen(recipe)}
    >
      <div className="relative aspect-square overflow-hidden">
        <img 
          src={recipe.image} alt={recipe.title} 
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        <div className="absolute top-5 right-5 flex flex-col gap-3 z-10">
          {!isAuthor ? (
            <button 
              data-testid={`like-btn-${recipe.id}`}
              onClick={handleLikeClick}
              className={`p-4 rounded-[20px] transition-all border-2 active:scale-90 shadow-xl ${
                isLiked 
                  ? 'bg-rose-500 text-white border-rose-400' 
                  : 'bg-white/90 dark:bg-stone-800/90 text-stone-700 dark:text-white border-white/50 dark:border-stone-600 hover:bg-rose-500 hover:text-white hover:border-rose-400'
              }`}
            >
              <Heart size={20} fill={isLiked ? "currentColor" : "none"} />
            </button>
          ) : (
            <div className="flex flex-col gap-3">
              <button 
                onClick={(e) => { e.stopPropagation(); if (onEdit) onEdit(recipe); }} 
                className="p-4 rounded-[20px] bg-white/90 dark:bg-stone-800/90 text-stone-700 dark:text-white hover:bg-primary hover:text-white transition-all border-2 border-white/50 dark:border-stone-600 shadow-xl"
              >
                <Edit3 size={18} />
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); if (onDelete) onDelete(recipe.id); }} 
                className="p-4 rounded-[20px] bg-white/90 dark:bg-stone-800/90 text-stone-700 dark:text-white hover:bg-rose-500 hover:text-white transition-all border-2 border-white/50 dark:border-stone-600 shadow-xl"
              >
                <Trash2 size={18} />
              </button>
            </div>
          )}
        </div>

        <div className="absolute bottom-5 left-5 bg-white/95 dark:bg-stone-800/95 px-4 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest text-stone-700 dark:text-white flex items-center gap-2 shadow-xl border border-white/20">
          <Clock size={14} className="text-primary" />
          {recipe.time}
        </div>
      </div>
      
      <div className="p-8">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-[9px] uppercase tracking-[0.2em] font-black text-primary bg-primary/10 px-3 py-1.5 rounded-xl">
            {recipe.tags?.[0] || 'Gourmet'}
          </span>
          <span className={`text-[9px] uppercase tracking-[0.2em] font-black px-3 py-1.5 rounded-xl border flex items-center gap-1 ${recipe.difficulty === 'Fácil' ? 'bg-emerald-50 text-emerald-600 border-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-800' : 'bg-amber-50 text-amber-600 border-amber-100 dark:bg-amber-900/20 dark:border-amber-800'}`}>
            <Flame size={10}/> {recipe.difficulty}
          </span>
        </div>
        
        <h3 className={`${settings?.largeText ? 'text-3xl' : 'text-2xl'} font-bold text-stone-900 dark:text-stone-100 mb-2 leading-tight group-hover:text-primary transition-colors heading-serif`}>
          {recipe.title}
        </h3>
        
        <p className="text-stone-500 dark:text-stone-400 text-sm line-clamp-2 mb-8 leading-relaxed font-medium">
          {recipe.description}
        </p>
        
        <div className="flex items-center justify-between pt-6 border-t border-stone-100 dark:border-stone-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-stone-100 dark:bg-stone-800 overflow-hidden">
              <img src={recipe.author_avatar || 'https://api.dicebear.com/7.x/avataaars/svg?seed=Chef'} className="w-full h-full object-cover" alt={recipe.author} />
            </div>
            <span className="text-[10px] font-black uppercase tracking-widest text-stone-600 dark:text-stone-300">@{recipe.author}</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 font-black">
              <Heart size={14} fill={isLiked ? "currentColor" : "none"} className={`transition-transform duration-300 ${isLiked ? 'text-rose-500 scale-110' : 'text-stone-400 dark:text-stone-500'}`} />
              <span className="text-[10px] text-stone-600 dark:text-stone-400">{recipe.likes_count || 0}</span>
            </div>
            <div className="bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 p-2.5 rounded-2xl group-hover:bg-primary group-hover:text-white transition-all shadow-lg">
              <ChevronRight size={18} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
